import {
  useEffect,
  useState,
  type FormEvent,
} from "react";
import {
  ArrowRight,
  Church,
  Eye,
  EyeOff,
  LockKeyhole,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

import { AuthLayout } from "@/components/auth/AuthLayout";
import { useToast } from "@/hooks/useToast";
import {
  consumeSessionDestination,
  consumeSessionMessage,
} from "@/lib/session-expiration";

import { useAuth } from "../hooks/useAuth";

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showToast } = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] =
    useState(false);
  const [errorMessage, setErrorMessage] =
    useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] =
    useState(false);

  useEffect(() => {
    const sessionMessage = consumeSessionMessage();

    if (!sessionMessage) {
      return;
    }

    showToast({
      title: "Session expired",
      description: sessionMessage,
      variant: "warning",
    });
  }, [showToast]);

  useEffect(() => {
    setErrorMessage(null);
  }, [email, password]);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    const normalizedEmail =
      email.trim().toLowerCase();

    if (!normalizedEmail || !password) {
      setErrorMessage(
        "Enter your email address and password.",
      );

      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      await login({
        email: normalizedEmail,
        password,
      });

      const destination =
        consumeSessionDestination("/");

      navigate(destination, {
        replace: true,
      });
    } catch {
      setErrorMessage(
        "The email or password you entered is incorrect.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthLayout
      variant="platform"
      workspaceLabel="Church Workspace"
      brandIcon={Church}
      cardIcon={LockKeyhole}
      eyebrow="Ministry Intelligence"
      heroTitle="Know who needs you before Sunday."
      heroDescription="MinistryIQ continuously monitors attendance, engagement, and follow-up so your ministry team can care for people before they quietly drift away."
      formTitle="Welcome back"
      formDescription="Sign in to access your MinistryIQ church workspace."
      footer="Need help accessing your workspace? Contact your church administrator."
    >
      {errorMessage && (
        <div
          role="alert"
          className="mt-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3"
        >
          <p className="text-sm font-medium text-rose-700">
            {errorMessage}
          </p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="mt-8 space-y-5"
      >
        <div>
          <label
            htmlFor="church-email"
            className="text-sm font-semibold text-slate-700"
          >
            Email address
          </label>

          <input
            id="church-email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(event) =>
              setEmail(event.target.value)
            }
            placeholder="you@example.com"
            disabled={isSubmitting}
            className="mt-2 h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100"
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-4">
            <label
              htmlFor="church-password"
              className="text-sm font-semibold text-slate-700"
            >
              Password
            </label>

            <Link
              to="/forgot-password"
              className="text-sm font-medium text-indigo-600 transition hover:text-indigo-500"
            >
              Forgot your password?
            </Link>
          </div>

          <div className="relative mt-2">
            <input
              id="church-password"
              type={
                showPassword
                  ? "text"
                  : "password"
              }
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) =>
                setPassword(event.target.value)
              }
              disabled={isSubmitting}
              className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 pr-12 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100"
            />

            <button
              type="button"
              onClick={() =>
                setShowPassword(
                  (current) => !current,
                )
              }
              aria-label={
                showPassword
                  ? "Hide password"
                  : "Show password"
              }
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-400 transition hover:text-slate-700"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              Signing in...
            </>
          ) : (
            <>
              Sign in to Workspace
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </AuthLayout>
  );
}
